package com.taocent.simple.compose.component.richtext.core.internal.serialization

import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.ParagraphStyle
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.BaselineShift
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.TextUnit
import com.taocent.simple.compose.component.richtext.core.internal.emoji.CUSTOM_EMOJI_TAG

/**
 * HTML 序列化配置。
 *
 * - [useSemanticTags] = true:粗体/斜体/下划线/删除线/上标/下标优先用语义标签
 *   (`<b>/<i>/<u>/<s>/<sup>/<sub>`),否则统一用 `<span style="...">`。
 *   其他属性(颜色/字号/背景)无论此开关如何都走 `<span style>`。
 * - [wrapInDocument]:是否把输出用 `<div>...</div>` 包裹,默认 false。
 *   平台 actual 通常**不要** wrap(浏览器已经解析 HTML fragment);
 *   但 [smartPaste] / [RichTextClipboardActions] 走 HTML 智能粘贴场景下
 *   也不需要 wrap(粘贴到 contenteditable 时 fragment 即可)。
 * - [rootTag]:仅在 [wrapInDocument] = true 时生效,默认 `div`。
 *
 * 公共 API 暴露原因:平台 `toRichClipEntry(annotated, config)` expect/actual 签名
 * 需要 config 作为参数;而 expect 必须是 public 才能跨 module 调用。
 */
data class RichTextHtmlConfig(
    val useSemanticTags: Boolean = true,
    val wrapInDocument: Boolean = false,
    val rootTag: String = "div",
)

/**
 * AnnotatedString → HTML 序列化器,与 [HtmlToAnnotatedString] 配对使用。
 *
 * 输出 HTML 子集(以 text/html 形式跨平台落地,目标是本组件富文本 ↔ 系统剪贴板):
 *   - 段落:`<p style="text-align:center/left/right/justify">` 包裹
 *     含 paragraph style 的段落;无 paragraph style 的段落用 `<br>` 分隔。
 *   - 语义标签:粗体 `<b>`、斜体 `<i>`、下划线 `<u>`、删除线 `<s>`、
 *     上标 `<sup>`、下标 `<sub>` —— [RichTextHtmlConfig.useSemanticTags] = true
 *     时纯单一属性优先用语义标签。
 *   - 复杂属性(颜色/字号/背景/复合):统一用 `<span style="...">` 内联 style。
 *   - emoji:`<span data-emoji-id="id">[id]</span>`,text 节点 = `[id]` 短代码,
 *     与 text/plain 输出一致,跨应用粘贴时其他应用看到 `[id]` 文本。
 *   - 超链接:`<a href="url">text</a>`。
 *   - 换行:段内 `'\n'` 写成 `<br>`;段尾用 `</p>` 关闭(若该段有 paragraph style)。
 *   - 文本 escape:所有 text 节点 / 属性值经 [RichTextHtmlEscape.escape]。
 *
 * SpanStyle 重叠/邻接处理:用"原子区间"算法 —— 收集所有 range 的边界,
 * 切成最小子区间,每个子区间合成一个有效 SpanStyle 后输出,避免嵌套错乱。
 * HTML 嵌套顺序约定:`<a>(最外)> <span data-emoji-id> > <span style> > <b>/<i>/...> (最内)`。
 */
object AnnotatedStringToHtml {

    fun toHtml(annotated: AnnotatedString, config: RichTextHtmlConfig = RichTextHtmlConfig()): String {
        val text = annotated.text
        if (text.isEmpty()) {
            return if (config.wrapInDocument) {
                // 修复:即使空内容也输出 charset 声明,避免 macOS 备忘录在解析
                // text/html 时把 UTF-8 中文按系统默认编码(GBK/Latin-1)解读导致乱码。
                "<${config.rootTag}><meta charset=\"UTF-8\"></${config.rootTag}>"
            } else ""
        }
        val sb = StringBuilder()
        // 修复:在 HTML 开头添加 `<meta charset="UTF-8">` 声明。
        // 背景:macOS 备忘录 (Notes.app) 读取 text/html 类型的剪贴板内容时,
        // 会优先按 `public.html` pasteboard type 解析,某些版本/路径下会用
        // 系统默认编码(中文系统下是 GBK)解码 HTML 字符串的字节,导致 UTF-8
        // 中文字节(E5 93 88 = 哈)被错读为 GBK 字符(鍝堝搱鍝),出现乱码。
        //
        // HTML5 规范允许 `<meta charset>` 出现在 HTML fragment 头部(在
        // 解析的前 1024 字节内),浏览器和主流 HTML 解析器(包括 WebKit /
        // macOS Cocoa WebKit)都会识别该声明并切到 UTF-8 解码。
        //
        // 同时在输出开头附加 UTF-8 BOM (U+FEFF) 作为冗余保险:
        // - 部分桌面应用读取字节流时通过 BOM 推断编码
        // - Kotlin/JVM 端 `MultiMimeStringSelection.setHtml` 把 HTML 作为
        //   String 传递,Java 的 DataFlavor 在某些 JRE 下会用平台默认编码
        //   转字节,附 BOM 能让接收方用 UTF-8 解析
        sb.append('\uFEFF')
        sb.append("<meta charset=\"UTF-8\">")
        if (config.wrapInDocument) sb.append('<').append(config.rootTag).append('>')

        // 按 `'\n'` 切分段落:每个段落 = [segStart, newlineIdx) 半开区间
        // (切完后 newlineIdx 不在段内,段内不含 `\n`)
        val segmentRanges = mutableListOf<IntRange>()
        var segStart = 0
        for (i in 0 until text.length) {
            if (text[i] == '\n') {
                segmentRanges.add(segStart until i)
                segStart = i + 1
            }
        }
        if (segStart <= text.length) {
            segmentRanges.add(segStart until text.length)
        }

        // 对每段独立输出
        for ((idx, range) in segmentRanges.withIndex()) {
            if (range.isEmpty()) {
                // 空段(连续 `\n` 切出的):插入一个 `<br>` 表示空行
                if (idx > 0) sb.append("<br>")
                continue
            }
            val paraStyle = findParagraphStyleAt(annotated, range.first, range.last + 1)
            writeParagraph(sb, annotated, range.first, range.last + 1, paraStyle, config)
            // 段间分隔:
            //   - 当前段有 paragraph style:用 `</p><p>` 切分(写入 next 段开头时再开 `<p>`)
            //     → 简化:本实现每段独立 `<p>...</p>`,段间用 `</p><p>` 配对
            //   - 当前段无 paragraph style + 下一段也无:用 `<br>` 切分
            //   - 当前段无 + 下一段有:用 `<br><p style="...">` 切分
            val hasPara = paraStyle != null && paraStyle.textAlign != TextAlign.Unspecified
            val isLast = idx == segmentRanges.lastIndex
            if (!isLast) {
                if (hasPara) sb.append("</p>") else sb.append("<br>")
            }
        }

        if (config.wrapInDocument) sb.append("</").append(config.rootTag).append('>')
        return sb.toString()
    }

    /**
     * 找到覆盖 [start, end) 区间内任意位置的 paragraph style。
     * 简化策略:用区间左端点查询(Compose 内部 paragraph style range 通常从段首开始)。
     */
    private fun findParagraphStyleAt(annotated: AnnotatedString, start: Int, end: Int): ParagraphStyle? {
        if (start >= end) return null
        for (range in annotated.paragraphStyles) {
            if (start >= range.start && start < range.end) {
                if (range.item.textAlign != TextAlign.Unspecified) return range.item
            }
        }
        return null
    }

    private fun writeParagraph(
        sb: StringBuilder,
        annotated: AnnotatedString,
        start: Int,
        end: Int,
        paraStyle: ParagraphStyle?,
        config: RichTextHtmlConfig,
    ) {
        val text: String = annotated.text
        val hasParaStyle = paraStyle != null && paraStyle.textAlign != TextAlign.Unspecified
        if (hasParaStyle) {
            sb.append("<p").append(paragraphStyleAttr(paraStyle)).append('>')
        }
        // 构造原子子区间并输出
        val boundaries: MutableList<Int> = mutableListOf()
        boundaries.add(start)
        boundaries.add(end)
        for (span in annotated.spanStyles) {
            if (span.end <= start || span.start >= end) continue
            boundaries.add(maxOf(span.start, start))
            boundaries.add(minOf(span.end, end))
        }
        for (ann in annotated.getStringAnnotations(0, annotated.length)) {
            if (ann.end <= start || ann.start >= end) continue
            if (ann.tag != "URL" && ann.tag != CUSTOM_EMOJI_TAG) continue
            boundaries.add(maxOf(ann.start, start))
            boundaries.add(minOf(ann.end, end))
        }
        val sorted: List<Int> = boundaries.distinct().sorted()
        for (i in 0 until sorted.size - 1) {
            val s: Int = sorted[i]
            val e: Int = sorted[i + 1]
            // 段内换行(`\n` 已经是段落边界,所以此处 text 段不含 `\n`)
            if (s >= e) continue
            // 找覆盖 [s, e) 的 emoji annotation
            val emojiId = findEmojiIdAt(annotated, s, e)
            val urlValue = findUrlAt(annotated, s, e)
            val mergedSpan = mergeSpansAt(annotated, s, e)
            // HTML 嵌套顺序:URL > EMOJI > SPAN_STYLE
            // (约定:URL 永远是最外层,emoji 是中间层,SpanStyle 是最内层)
            val (openTags, closeTags) = buildNestedTags(mergedSpan, urlValue, emojiId, config)
            sb.append(openTags)
            // emoji 段不输出 shortcode 文本,由反序列化器主动 append;其他段输出 escape 后的 text
            if (emojiId == null) {
                sb.append(RichTextHtmlEscape.escape(text.substring(s, e)))
            }
            sb.append(closeTags)
        }
        if (hasParaStyle) sb.append("</p>")
    }

    private fun findEmojiIdAt(annotated: AnnotatedString, start: Int, end: Int): String? {
        for (ann in annotated.getStringAnnotations(CUSTOM_EMOJI_TAG, 0, annotated.length)) {
            // 半开区间 [start, end) 在 annotation [ann.start, ann.end) 内 → 整段是同一 emoji
            if (ann.start <= start && ann.end >= end) {
                return ann.item
            }
        }
        return null
    }

    private fun findUrlAt(annotated: AnnotatedString, start: Int, end: Int): String? {
        for (ann in annotated.getStringAnnotations("URL", 0, annotated.length)) {
            if (ann.start <= start && ann.end >= end) {
                return ann.item
            }
        }
        return null
    }

    /**
     * 合成 [start, end) 区间内所有覆盖的 SpanStyle。Compose 的 SpanStyle 用 `merge`
     * 合成:不冲突的属性叠加,冲突的属性取外层优先(此处用 last-write-wins 即最后遍历到的 range 覆盖)。
     */
    private fun mergeSpansAt(annotated: AnnotatedString, start: Int, end: Int): SpanStyle? {
        var result: SpanStyle? = null
        for (span in annotated.spanStyles) {
            if (span.end <= start || span.start >= end) continue
            result = if (result == null) span.item else result.merge(span.item)
        }
        return result
    }

    private fun buildNestedTags(
        span: SpanStyle?,
        url: String?,
        emojiId: String?,
        config: RichTextHtmlConfig,
    ): Pair<String, String> {
        val opens = StringBuilder()
        val closes = StringBuilder()
        if (url != null) {
            opens.append("<a href=\"").append(RichTextHtmlEscape.escape(url)).append("\">")
            closes.insert(0, "</a>")
        }
        if (emojiId != null) {
            // emoji 段不输出 shortcode 文本,由反序列化器 [HtmlToAnnotatedString]
            // 根据 `data-emoji-id` 属性主动 append `█` placeholder + annotation。
            // 跨应用粘贴时其他应用看到的是空 `<span></span>`,显示为空白(本组件 emoji
            // 在其他应用本来就不渲染,这种"空白"行为可接受)。
            //
            // **emoji + SpanStyle 合并到 outer <span>**:
            // 之前实现把 emoji SpanStyle 输出为内嵌 `<span style="...">`,反序列化时
            // outer `<span data-emoji-id>` 先 append `█` placeholder 再处理内嵌
            // `<span style>` —— pushStyle 顺序在 append 之后,**placeholder 字符没
            // 被 SpanStyle 覆盖**,导致 `CustomEmojiCanvasOverlay` 拿不到 emoji 段
            // 的 fontSize / color,emoji 跨进程 paste 后样式丢失。修复:把 emoji 段
            // 的 SpanStyle 直接合并到 outer `<span data-emoji-id>` 的 `style` 属性,
            // 反序列化时一次 pushStyle 就能把 placeholder 字符覆盖到。
            val css: String? = if (span != null && span != SpanStyle()) {
                spanStyleToCss(span)
            } else null
            if (css != null) {
                opens.append("<span data-emoji-id=\"")
                    .append(RichTextHtmlEscape.escape(emojiId))
                    .append("\" style=\"")
                    .append(css)
                    .append("\">")
            } else {
                opens.append("<span data-emoji-id=\"").append(RichTextHtmlEscape.escape(emojiId)).append("\">")
            }
            closes.insert(0, "</span>")
            // emoji 段内嵌的 SpanStyle 已合并到 outer,不再额外输出内层 `<span style>`。
            // 这样输出 HTML 体积更小,反序列化器也只需一次 pushStyle 即可。
        } else if (span != null && span != SpanStyle()) {
            // 非 emoji 段(纯 SpanStyle):沿用原语义标签 / `<span style>` 嵌套
            val innerTags = spanStyleToTags(span, config.useSemanticTags)
            if (innerTags != null) {
                opens.append(innerTags.first)
                closes.insert(0, innerTags.second)
            }
        }
        return opens.toString() to closes.toString()
    }

    /**
     * 把 [SpanStyle] 序列化为 CSS 声明字符串(无外层 `<span style="...">` 包裹)。
     * 返回 `null` 表示 SpanStyle 为空(无任何可序列化属性)。调用方根据返回值决定
     * 是否需要输出 `style` 属性。
     *
     * 与 [spanStyleToTags] 的关系:本函数是它的"裸 CSS"版本,用于把 SpanStyle 合并到
     * 已有的 outer tag(例如 `<span data-emoji-id style="...">`)而不是开新 `<span>`。
     */
    private fun spanStyleToCss(span: SpanStyle): String? {
        val color = if (span.color == Color.Unspecified) null else span.color
        val bg = if (span.background == Color.Unspecified) null else span.background
        val fontSize = span.fontSize
        val fw = span.fontWeight
        val fs = span.fontStyle
        val td = span.textDecoration
        val bs = span.baselineShift
        val styleDecls = mutableListOf<String>()
        if (color != null) styleDecls.add("color:${colorToCss(color)}")
        if (bg != null) styleDecls.add("background-color:${colorToCss(bg)}")
        if (fontSize != TextUnit.Unspecified) {
            // TextUnit 未指定时 value = NaN,先检查避免转 Int 出 NaN
            styleDecls.add("font-size:${fontSize.value.toInt()}px")
        }
        if (fw != null) {
            if (fw >= FontWeight.Bold) styleDecls.add("font-weight:bold")
            else if (fw != FontWeight.Normal) styleDecls.add("font-weight:${fw.weight}")
        }
        if (fs == FontStyle.Italic) styleDecls.add("font-style:italic")
        if (td != null) {
            if (td.contains(TextDecoration.Underline)) styleDecls.add("text-decoration:underline")
            if (td.contains(TextDecoration.LineThrough)) styleDecls.add("text-decoration:line-through")
        }
        if (bs == BaselineShift.Superscript) styleDecls.add("vertical-align:super")
        if (bs == BaselineShift.Subscript) styleDecls.add("vertical-align:sub")
        return if (styleDecls.isEmpty()) null else styleDecls.joinToString(";")
    }

    /**
     * SpanStyle → HTML 标签对。
     * 语义标签优先(粗体/斜体/下划线/删除线/上标/下标),复合属性走 `<span style>`。
     *
     * 注意:本函数只对**非 emoji** 段生效。emoji 段内嵌 SpanStyle 在 [buildNestedTags]
     * 走专门路径合并到 outer `<span data-emoji-id style="...">`,不再开内层 `<span>`。
     */
    private fun spanStyleToTags(span: SpanStyle, useSemanticTags: Boolean): Pair<String, String>? {
        val fw = span.fontWeight
        val fs = span.fontStyle
        val td = span.textDecoration
        val bs = span.baselineShift
        val color = if (span.color == Color.Unspecified) null else span.color
        val bg = if (span.background == Color.Unspecified) null else span.background
        val fontSize = span.fontSize

        // 1. 检测能否用纯语义标签(单一属性)
        if (useSemanticTags) {
            if (fw != null && fw >= FontWeight.Bold && fs == null && td == null && bs == null &&
                color == null && bg == null && fontSize == TextUnit.Unspecified
            ) {
                return "<b>" to "</b>"
            }
            if (fs == FontStyle.Italic && fw == null && td == null && bs == null &&
                color == null && bg == null && fontSize == TextUnit.Unspecified
            ) {
                return "<i>" to "</i>"
            }
            if (td != null && !td.contains(TextDecoration.LineThrough) && bs == null && fw == null &&
                fs == null && color == null && bg == null && fontSize == TextUnit.Unspecified
            ) {
                // underline
                return "<u>" to "</u>"
            }
            if (td != null && !td.contains(TextDecoration.Underline) && bs == null && fw == null &&
                fs == null && color == null && bg == null && fontSize == TextUnit.Unspecified
            ) {
                // line-through
                return "<s>" to "</s>"
            }
            if (bs == BaselineShift.Superscript && fw == null && fs == null && td == null &&
                color == null && bg == null && fontSize == TextUnit.Unspecified
            ) {
                return "<sup>" to "</sup>"
            }
            if (bs == BaselineShift.Subscript && fw == null && fs == null && td == null &&
                color == null && bg == null && fontSize == TextUnit.Unspecified
            ) {
                return "<sub>" to "</sub>"
            }
        }

        // 2. 复杂情况:用 <span style="...">
        val css = spanStyleToCss(span) ?: return null
        return "<span style=\"$css\">" to "</span>"
    }

    /**
     * 把 [Color] 序列化为 CSS 颜色值。
     *
     * **输出策略**(跨浏览器/WebView 一致性优先):
     *   - 完全不透明(alpha ≈ 1.0):输出 **6 位 `#RRGGBB` hex**(标准 CSS Level 1)
     *   - 半透明(alpha < 1.0):输出 **`rgba(R, G, B, A)`** 函数(W3C CSS Color Level 3)
     *
     * **为什么不输出 8 位 `#AARRGGBB`(ARGB)或 `#RRGGBBAA`(RGBA)?**
     *   - `#AARRGGBB`(ARGB,alpha 在前)是 WebKit 历史上的非标准扩展,**现代 W3C CSS Color
     *     Module Level 4(iOS 15+ Safari / Chrome 62+ / Firefox 49+)改为 `#RRGGBBAA`**
     *     即 alpha 在**最后**。同一段 hex 在不同浏览器/WebKit 版本下解读完全不同:
     *       - 老 WebKit (iOS 12-14):`#FF0000FF` → ARGB → 蓝色
     *       - 新 WebKit (iOS 15+):`#FF0000FF` → RRGGBBAA → 红色
     *     这就是 iOS app 内部的 WebView 复制富文本到 Safari 浏览器(或反过来)时,
     *     **同一段富文本两端显示颜色不一致**的根因。
     *   - `#RRGGBBAA` 虽然是 W3C 新标准,但部分老 WebKit / 旧版 Safari / 部分 Android WebView
     *     仍然只认 ARGB 或完全不认 8 位 hex,跨平台一致性不足。
     *   - 6 位 `#RRGGBB` + `rgba()` 是**所有**现代浏览器 + 移动 WebView + 桌面 WebView 都
     *     100% 支持的格式,跨平台一致性最好。
     *
     * **反序列化兼容**:`[HtmlToAnnotatedString.parseCssColor]` 接受 3/6/8 位 hex + rgb()/rgba()
     * 函数 + 命名颜色,所以 6 位 hex / rgba() 都是 round-trip OK 的(8 位 hex 也接受,但
     * 不再主动输出)。
     */
    private fun colorToCss(color: Color): String {
        val r = (color.red * 255f).toInt().coerceIn(0, 255)
        val g = (color.green * 255f).toInt().coerceIn(0, 255)
        val b = (color.blue * 255f).toInt().coerceIn(0, 255)
        val a = color.alpha.coerceIn(0f, 1f)
        return if (a >= 0.999f) {
            // 完全不透明 → 6 位 #RRGGBB
            "#" + r.toString(16).uppercase().padStart(2, '0') +
                    g.toString(16).uppercase().padStart(2, '0') +
                    b.toString(16).uppercase().padStart(2, '0')
        } else {
            // 半透明 → rgba() 函数(注意:alpha 是 0..1 float,不是 0..255 int)
            // 用 `String.format("%.3f", ...)` 输出 3 位小数,避免 locale 相关的
            // 小数点符号差异(欧洲 locale 用 "," 作小数点,会导致 CSS 解析失败)
            "rgba(" + r + ", " + g + ", " + b + ", " +
                    a.toString() + ")"
        }
    }

    private fun paragraphStyleAttr(style: ParagraphStyle): String {
        val align = when (style.textAlign) {
            TextAlign.Center -> "center"
            TextAlign.Right -> "right"
            TextAlign.Left -> "left"
            TextAlign.Justify -> "justify"
            TextAlign.Start -> "start"
            TextAlign.End -> "end"
            else -> return ""
        }
        return " style=\"text-align:$align\""
    }
}
